type ZiarcadiaCompatWindow = Window &
    typeof globalThis & {
        __ziarcadiaBrowserCompatibilityInstalled?: boolean;
    };

type PointerCaptureElement = Element & {
    setPointerCapture?: (pointerId: number) => void;
    releasePointerCapture?: (pointerId: number) => void;
    hasPointerCapture?: (pointerId: number) => boolean;
};

type CompatibilityAnimation = Animation & {
    _finish?: () => void;
};

type MutableAnimationFields = {
    playState: AnimationPlayState;
    ready: Promise<Animation>;
};

const ACTIVE_ANIMATIONS = new WeakMap<Element, Set<CompatibilityAnimation>>();
const FALLBACK_POINTER_ID = 1;
const TOUCH_MOUSE_SUPPRESSION_MS = 700;

const getWindow = (): ZiarcadiaCompatWindow | null =>
    typeof window === "undefined" || typeof document === "undefined"
        ? null
        : (window as ZiarcadiaCompatWindow);

const getAnimationDurationMs = (options?: number | KeyframeAnimationOptions): number => {
    if (typeof options === "number") {
        return Math.max(0, options);
    }

    const duration = options?.duration;

    if (typeof duration === "number") {
        return Math.max(0, duration);
    }

    return 0;
};

const getAnimationFillMode = (options?: number | KeyframeAnimationOptions): FillMode | undefined =>
    typeof options === "number" ? undefined : options?.fill;

const getFinalKeyframe = (keyframes: Keyframe[] | PropertyIndexedKeyframes | null): Keyframe | null => {
    if (!keyframes) {
        return null;
    }

    if (Array.isArray(keyframes)) {
        return keyframes[keyframes.length - 1] ?? null;
    }

    const finalKeyframe: Keyframe = {};

    Object.entries(keyframes).forEach(([property, value]) => {
        if (!Array.isArray(value)) {
            finalKeyframe[property] = value as string | number | null;
            return;
        }

        finalKeyframe[property] = value[value.length - 1] as string | number | null;
    });

    return finalKeyframe;
};

const applyFinalKeyframe = (element: Element, keyframes: Keyframe[] | PropertyIndexedKeyframes | null) => {
    if (!(element instanceof HTMLElement || element instanceof SVGElement)) {
        return;
    }

    const finalKeyframe = getFinalKeyframe(keyframes);

    if (!finalKeyframe) {
        return;
    }

    Object.entries(finalKeyframe).forEach(([property, value]) => {
        if (value === null || value === undefined || property === "offset" || property === "easing" || property === "composite") {
            return;
        }

        const styleValue = Array.isArray(value) ? value[value.length - 1] : value;

        if (styleValue !== null && styleValue !== undefined) {
            element.style.setProperty(property.replace(/[A-Z]/g, (match) => `-${match.toLowerCase()}`), String(styleValue));
        }
    });
};

const trackFallbackAnimation = (element: Element, animation: CompatibilityAnimation) => {
    const animations = ACTIVE_ANIMATIONS.get(element) ?? new Set<CompatibilityAnimation>();

    animations.add(animation);
    ACTIVE_ANIMATIONS.set(element, animations);

    const remove = () => {
        animations.delete(animation);
    };

    const finish = animation._finish;

    animation._finish = () => {
        remove();
        finish?.();
    };

    const cancel = animation.cancel.bind(animation);
    animation.cancel = () => {
        remove();
        cancel();
    };
};

const installWebAnimationFallback = () => {
    if (typeof Element === "undefined") {
        return;
    }

    if (typeof Element.prototype.getAnimations !== "function") {
        Element.prototype.getAnimations = function getAnimationsFallback() {
            return Array.from(ACTIVE_ANIMATIONS.get(this) ?? []);
        };
    }

    if (typeof Element.prototype.animate === "function") {
        return;
    }

    Element.prototype.animate = function animateFallback(
        keyframes: Keyframe[] | PropertyIndexedKeyframes | null,
        options?: number | KeyframeAnimationOptions
    ): Animation {
        const element = this;
        const duration = getAnimationDurationMs(options);
        const fill = getAnimationFillMode(options);
        let playState: AnimationPlayState = "running";
        let finishTimer: number | null = null;
        let resolveFinished: (animation: Animation) => void = () => undefined;
        let rejectFinished: (error: DOMException) => void = () => undefined;

        const animation = {
            currentTime: 0,
            effect: null,
            finished: new Promise<Animation>((resolve, reject) => {
                resolveFinished = resolve;
                rejectFinished = reject;
            }),
            id: "",
            oncancel: null,
            onfinish: null,
            onremove: null,
            pending: false,
            playState,
            playbackRate: 1,
            ready: Promise.resolve(null as unknown as Animation),
            replaceState: "active",
            startTime: null,
            timeline: null,
            addEventListener: () => undefined,
            cancel: () => {
                if (finishTimer !== null) {
                    window.clearTimeout(finishTimer);
                    finishTimer = null;
                }

                playState = "idle";
                (animation as unknown as MutableAnimationFields).playState = playState;
                const error = new DOMException("Animation canceled", "AbortError");
                rejectFinished(error);
                animation.oncancel?.(new Event("cancel") as AnimationPlaybackEvent);
            },
            commitStyles: () => applyFinalKeyframe(element, keyframes),
            dispatchEvent: () => true,
            finish: () => {
                animation._finish?.();
            },
            pause: () => {
                playState = "paused";
                (animation as unknown as MutableAnimationFields).playState = playState;
            },
            persist: () => undefined,
            play: () => {
                playState = "running";
                (animation as unknown as MutableAnimationFields).playState = playState;
            },
            removeEventListener: () => undefined,
            reverse: () => undefined,
            updatePlaybackRate: (playbackRate: number) => {
                animation.playbackRate = playbackRate;
            },
            _finish: () => {
                if (finishTimer !== null) {
                    window.clearTimeout(finishTimer);
                    finishTimer = null;
                }

                if (fill === "forwards" || fill === "both") {
                    applyFinalKeyframe(element, keyframes);
                }

                playState = "finished";
                (animation as unknown as MutableAnimationFields).playState = playState;
                resolveFinished(animation);
                animation.onfinish?.(new Event("finish") as AnimationPlaybackEvent);
            }
        } as CompatibilityAnimation;

        (animation as unknown as MutableAnimationFields).ready = Promise.resolve(animation);
        finishTimer = window.setTimeout(() => {
            animation._finish?.();
        }, duration);
        trackFallbackAnimation(element, animation);

        return animation;
    };
};

const installPointerCaptureFallback = () => {
    if (typeof Element === "undefined") {
        return;
    }

    const prototype = Element.prototype as PointerCaptureElement;

    prototype.setPointerCapture ??= () => undefined;
    prototype.releasePointerCapture ??= () => undefined;
    prototype.hasPointerCapture ??= () => false;
};

const definePointerProperty = (event: Event, property: string, value: unknown) => {
    try {
        Object.defineProperty(event, property, {
            configurable: true,
            enumerable: false,
            value
        });
    } catch {
        // Read-only event fields vary across old WebKit builds; missing pointer metadata is non-fatal.
    }
};

const createSyntheticPointerEvent = (
    type: string,
    point: Pick<MouseEvent, "clientX" | "clientY" | "screenX" | "screenY">,
    options: {
        sourceEvent: Event;
        pointerType: "mouse" | "touch";
        buttons: number;
        button?: number;
    }
): MouseEvent => {
    const sourceEvent = options.sourceEvent;
    const mouseEvent = new MouseEvent(type, {
        bubbles: true,
        cancelable: sourceEvent.cancelable,
        composed: true,
        clientX: point.clientX,
        clientY: point.clientY,
        screenX: point.screenX,
        screenY: point.screenY,
        button: options.button ?? 0,
        buttons: options.buttons,
        ctrlKey: "ctrlKey" in sourceEvent ? Boolean(sourceEvent.ctrlKey) : false,
        altKey: "altKey" in sourceEvent ? Boolean(sourceEvent.altKey) : false,
        shiftKey: "shiftKey" in sourceEvent ? Boolean(sourceEvent.shiftKey) : false,
        metaKey: "metaKey" in sourceEvent ? Boolean(sourceEvent.metaKey) : false
    });

    definePointerProperty(mouseEvent, "pointerId", FALLBACK_POINTER_ID);
    definePointerProperty(mouseEvent, "pointerType", options.pointerType);
    definePointerProperty(mouseEvent, "isPrimary", true);
    definePointerProperty(mouseEvent, "width", options.pointerType === "touch" ? 24 : 1);
    definePointerProperty(mouseEvent, "height", options.pointerType === "touch" ? 24 : 1);
    definePointerProperty(mouseEvent, "pressure", options.buttons > 0 ? 0.5 : 0);

    return mouseEvent;
};

const dispatchPointerEvent = (target: EventTarget | null, event: MouseEvent, sourceEvent: Event) => {
    if (!target) {
        return;
    }

    target.dispatchEvent(event);

    if (event.defaultPrevented && sourceEvent.cancelable) {
        sourceEvent.preventDefault();
    }
};

const installPointerEventBridge = (compatWindow: ZiarcadiaCompatWindow) => {
    if (typeof compatWindow.PointerEvent !== "undefined" || typeof TouchEvent === "undefined") {
        return;
    }

    let activeTouchId: number | null = null;
    let activeTouchTarget: EventTarget | null = null;
    let activeMouseTarget: EventTarget | null = null;
    let suppressMouseUntil = 0;

    const getTouchById = (touchList: TouchList, identifier: number | null): Touch | null => {
        if (identifier === null) {
            return touchList[0] ?? null;
        }

        for (let index = 0; index < touchList.length; index += 1) {
            const touch = touchList[index];

            if (touch.identifier === identifier) {
                return touch;
            }
        }

        return null;
    };

    document.addEventListener(
        "touchstart",
        (sourceEvent) => {
            if (activeTouchId !== null) {
                return;
            }

            const touch = sourceEvent.changedTouches[0];

            if (!touch) {
                return;
            }

            activeTouchId = touch.identifier;
            activeTouchTarget = touch.target;
            suppressMouseUntil = Date.now() + TOUCH_MOUSE_SUPPRESSION_MS;

            dispatchPointerEvent(
                activeTouchTarget,
                createSyntheticPointerEvent("pointerdown", touch, {
                    sourceEvent,
                    pointerType: "touch",
                    buttons: 1
                }),
                sourceEvent
            );
        },
        { capture: true, passive: false }
    );

    document.addEventListener(
        "touchmove",
        (sourceEvent) => {
            const touch = getTouchById(sourceEvent.changedTouches, activeTouchId);

            if (!touch) {
                return;
            }

            suppressMouseUntil = Date.now() + TOUCH_MOUSE_SUPPRESSION_MS;
            dispatchPointerEvent(
                activeTouchTarget ?? touch.target,
                createSyntheticPointerEvent("pointermove", touch, {
                    sourceEvent,
                    pointerType: "touch",
                    buttons: 1
                }),
                sourceEvent
            );
        },
        { capture: true, passive: false }
    );

    const endTouch = (sourceEvent: TouchEvent, type: "pointerup" | "pointercancel") => {
        const touch = getTouchById(sourceEvent.changedTouches, activeTouchId);

        if (!touch) {
            return;
        }

        suppressMouseUntil = Date.now() + TOUCH_MOUSE_SUPPRESSION_MS;
        dispatchPointerEvent(
            activeTouchTarget ?? touch.target,
            createSyntheticPointerEvent(type, touch, {
                sourceEvent,
                pointerType: "touch",
                buttons: 0
            }),
            sourceEvent
        );

        activeTouchId = null;
        activeTouchTarget = null;
    };

    document.addEventListener("touchend", (sourceEvent) => endTouch(sourceEvent, "pointerup"), {
        capture: true,
        passive: false
    });
    document.addEventListener("touchcancel", (sourceEvent) => endTouch(sourceEvent, "pointercancel"), {
        capture: true,
        passive: false
    });

    document.addEventListener(
        "mousedown",
        (sourceEvent) => {
            if (Date.now() < suppressMouseUntil || sourceEvent.button !== 0) {
                return;
            }

            activeMouseTarget = sourceEvent.target;
            dispatchPointerEvent(
                activeMouseTarget,
                createSyntheticPointerEvent("pointerdown", sourceEvent, {
                    sourceEvent,
                    pointerType: "mouse",
                    buttons: sourceEvent.buttons || 1,
                    button: sourceEvent.button
                }),
                sourceEvent
            );
        },
        { capture: true }
    );

    document.addEventListener(
        "mousemove",
        (sourceEvent) => {
            if (!activeMouseTarget || Date.now() < suppressMouseUntil) {
                return;
            }

            dispatchPointerEvent(
                activeMouseTarget,
                createSyntheticPointerEvent("pointermove", sourceEvent, {
                    sourceEvent,
                    pointerType: "mouse",
                    buttons: sourceEvent.buttons,
                    button: sourceEvent.button
                }),
                sourceEvent
            );
        },
        { capture: true }
    );

    document.addEventListener(
        "mouseup",
        (sourceEvent) => {
            if (!activeMouseTarget || Date.now() < suppressMouseUntil) {
                return;
            }

            dispatchPointerEvent(
                activeMouseTarget,
                createSyntheticPointerEvent("pointerup", sourceEvent, {
                    sourceEvent,
                    pointerType: "mouse",
                    buttons: sourceEvent.buttons,
                    button: sourceEvent.button
                }),
                sourceEvent
            );
            activeMouseTarget = null;
        },
        { capture: true }
    );
};

export const installBrowserCompatibilityLayer = () => {
    const compatWindow = getWindow();

    if (!compatWindow || compatWindow.__ziarcadiaBrowserCompatibilityInstalled) {
        return;
    }

    compatWindow.__ziarcadiaBrowserCompatibilityInstalled = true;
    installPointerCaptureFallback();
    installWebAnimationFallback();
    installPointerEventBridge(compatWindow);
};

export const readFileAsText = (file: File): Promise<string> => {
    if (typeof file.text === "function") {
        return file.text();
    }

    if (typeof FileReader === "undefined") {
        return Promise.reject(new Error("FileReader is unavailable."));
    }

    return new Promise((resolve, reject) => {
        const reader = new FileReader();

        reader.onerror = () => reject(reader.error ?? new Error("Could not read the file."));
        reader.onload = () => resolve(typeof reader.result === "string" ? reader.result : "");
        reader.readAsText(file);
    });
};

export const readFileAsArrayBuffer = (file: File): Promise<ArrayBuffer> => {
    if (typeof file.arrayBuffer === "function") {
        return file.arrayBuffer();
    }

    if (typeof FileReader === "undefined") {
        return Promise.reject(new Error("FileReader is unavailable."));
    }

    return new Promise((resolve, reject) => {
        const reader = new FileReader();

        reader.onerror = () => reject(reader.error ?? new Error("Could not read the file."));
        reader.onload = () =>
            reader.result instanceof ArrayBuffer
                ? resolve(reader.result)
                : reject(new Error("Could not read the file as a spreadsheet."));
        reader.readAsArrayBuffer(file);
    });
};

export const decodeAudioDataCompat = (context: AudioContext, arrayBuffer: ArrayBuffer): Promise<AudioBuffer> =>
    new Promise((resolve, reject) => {
        let settled = false;
        const finish = (buffer: AudioBuffer) => {
            if (!settled) {
                settled = true;
                resolve(buffer);
            }
        };
        const fail = (error: unknown) => {
            if (!settled) {
                settled = true;
                reject(error);
            }
        };

        try {
            const maybePromise = (
                context.decodeAudioData as (
                    audioData: ArrayBuffer,
                    successCallback?: DecodeSuccessCallback | null,
                    errorCallback?: DecodeErrorCallback | null
                ) => Promise<AudioBuffer> | void
            ).call(context, arrayBuffer, finish, fail);

            if (maybePromise && typeof maybePromise.then === "function") {
                maybePromise.then(finish).catch(fail);
            }
        } catch (error) {
            fail(error);
        }
    });

installBrowserCompatibilityLayer();
